import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compile } from 'path-to-regexp';
import checkUsEnglish from 'utils/checkUsEnglish';
import { LANGUAGE_EDITION, CONTENT_ITEM_SINGLE } from 'config/types/path';
import {
  FOOTER,
  FOOTER_COLUMN_1,
  FOOTER_COLUMN_2,
  FOOTER_COLUMN_3,
  FOOTER_COPYRIGHT,
} from 'config/types/menu';
import get from 'lodash/fp/get';
import getIs404 from 'selectors/getIs404';
import getPageTrackingData from 'selectors/getPageTrackingData';
import { pageType } from 'utils/tracking';
import { MENU, LANGUAGES } from 'config/types/prop';
import { getMenuTopLevel } from 'selectors/getMenu';
import withStyles from 'isomorphic-style-loader/withStyles';
import { options } from 'config/reactRedux';
import getMatch from 'selectors/getMatch';
import { getEdition } from 'selectors/getEdition';
import getPathMap from 'selectors/getPathMap';
import CallToAction from 'components/callToAction';
import FooterNav from 'components/footer/nav';
import SocialLinks from 'components/socialLinks';
import EditionPicker from 'components/editionPicker';
import { buildCTALink, buildHomepageLink } from 'utils/url';
import I18n from 'components/i18n';
import useTrackPageCompleted from 'hooks/useTrackPageCompleted';

import styles from './footer.css';

const Footer = ({
  footer,
  copyright,
  columnOne,
  columnTwo,
  columnThree,
  currentEdition,
  editions,
  currentLanguage,
  is404,
  pageTrackingData,
}) => {
  const { data: trackingData } = pageTrackingData;
  const footerElement = useTrackPageCompleted({
    trackingData,
    trackingEnabled: trackingData?.page_type !== pageType[CONTENT_ITEM_SINGLE],
    is404,
  });
  const goLink = buildCTALink(currentEdition);
  const ctaLabel = <I18n field="try-babbel" defaultText="Try Babbel" />;
  const ctaHeading = <I18n field="learn-a-new-language" defaultText="Learn a new language" />;

  return (
    <footer className={styles.footer} ref={footerElement}>
      <div className={styles.wrapper}>
        <EditionPicker
          editions={editions}
          currentEdition={currentEdition}
        />
        <div className={styles.moreBabbel}>
          <I18n
            field="footer-title"
            defaultText="More Babbel"
          />
        </div>
        <div className={styles.top}>
          <FooterNav
            headingTranslation="about-babbel"
            heading="About Babbel:"
            items={columnOne}
            currentLanguage={currentLanguage}
            name="columnOne"
          />
          <FooterNav
            headingTranslation="learn-languages-online"
            heading="Learn Languages Online:"
            items={columnTwo}
            currentLanguage={currentLanguage}
            name="columnTwo"
          />
          <FooterNav
            headingTranslation="this-page-in-other-languages"
            heading="This page in other languages:"
            items={columnThree}
            currentLanguage={currentLanguage}
            name="columnThree"
          />
          <CallToAction
            isInFooter
            heading={ctaHeading}
            text={ctaLabel}
            url={goLink}
          />
        </div>
      </div>
      <hr />
      <div className={styles.wrapper}>
        <div className={styles.bottom}>
          <div className={styles.copyright}>
            <FooterNav
              copyright
              currentLanguage={currentLanguage}
              items={copyright}
              name="bottomNav"
            />
          </div>
          <div className={styles.socialLinks}>
            <SocialLinks />
          </div>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  footer: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  copyright: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  columnOne: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  columnTwo: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  columnThree: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  currentEdition: LANGUAGES.isRequired,
  currentLanguage: LANGUAGES.isRequired,
  editions: PropTypes.object.isRequired,
  is404: PropTypes.bool.isRequired,
  pageTrackingData: PropTypes.object,
};

export const mapStateToProps = (state) => {
  const { params } = getMatch(state);
  const pathMap = getPathMap(state);
  // This could be turned into a selector if we ever need to reuse it
  const toLanguagePath = compile(pathMap[LANGUAGE_EDITION]);
  const editionNames = get('languages.translations', state);
  const currentEdition = getEdition(state);
  const pageTrackingData = getPageTrackingData(state);
  let editions = {};

  if (editionNames) {
    const editionsReducer = (acc, languageCode) => {
      const path = toLanguagePath({
        ...params,
        language: checkUsEnglish(languageCode),
      });

      const link = buildHomepageLink(languageCode, path);

      return ({
        ...acc,
        [languageCode]: {
          link,
          name: editionNames[languageCode],
        },
      });
    };
    editions = Object.keys(editionNames).reduce(editionsReducer, {});
  }

  return {
    footer: getMenuTopLevel(state, FOOTER),
    copyright: getMenuTopLevel(state, FOOTER_COPYRIGHT),
    columnOne: getMenuTopLevel(state, FOOTER_COLUMN_1),
    columnTwo: getMenuTopLevel(state, FOOTER_COLUMN_2),
    columnThree: getMenuTopLevel(state, FOOTER_COLUMN_3),
    currentEdition,
    currentLanguage: params.language,
    editions,
    pageTrackingData,
    is404: getIs404(state),
  };
};

const withRedux = connect(
  mapStateToProps,
  undefined,
  undefined,
  options,
);

const wrapWithStyles = withStyles(styles);

export default withRedux(
  wrapWithStyles(Footer),
);
